import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import { WsClient } from "@/lib/ws-client";
import { useWsRefetch } from "./useWsRefetch";

interface QueueCountsResponse {
  queues: { stepType: string; totalCount: number }[];
  // R-216 — authoritative documents-in-process total (Σ lane docs across
  // in-process steps; exported/completed excluded by construction). This is the
  // sidebar badge source and reconciles with Σ rendered lane docs.
}

const POLL_INTERVAL = 30_000;

// R-216 — broaden the badge's live refresh to every queue-mutating event so it
// stays in sync with the dashboard (which already subscribes to all four). A
// supervisor block/unblock or a document advancing between steps changes the
// in-process total just as much as a queue_update does.
// DE-1545 F-048 — also refresh on `batch_released` + `export_status` so the
// sidebar badge stays consistent with the dashboard after a release / export.
const QUEUE_WS_EVENTS = [
  "queue_update",
  "queue_block",
  "queue_unblock",
  "document_advance",
  "batch_released",
  "export_status",
] as const;

/** The Complete lane is terminal (F-031): exported docs awaiting purge. */
const TERMINAL_COMPLETE_LANE = "complete";

export function useQueueCounts(wsClient: WsClient | null) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [totalCount, setTotalCount] = useState(0);

  const fetchCounts = useCallback(async () => {
    try {
      const data = await api.get<QueueCountsResponse>("/queues");
      const map: Record<string, number> = {};
      let inProcess = 0;
      for (const q of data.queues) {
        map[q.stepType] = q.totalCount;
        // F-CAPTURE-020 — the terminal Complete lane (exported, awaiting purge) is
        // finished work, not "in process". /api/queues has never sent a
        // documentsInProcess total, so this sum IS the badge/header number.
        if (q.stepType !== TERMINAL_COMPLETE_LANE) inProcess += q.totalCount;
      }
      setCounts(map);
      setTotalCount(inProcess);
    } catch {
      // Silently ignore — nav badge is non-critical
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // WebSocket subscription (all queue-mutating events), with a polling
  // fallback while no client is connected.
  useWsRefetch(wsClient, QUEUE_WS_EVENTS, fetchCounts);
  useEffect(() => {
    if (wsClient) return;
    const timer = setInterval(fetchCounts, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [wsClient, fetchCounts]);

  return { counts, totalCount };
}
